/**
 * Task Group Hook
 *
 * Apollo Client hook for querying hierarchical task group templates
 * seeded from configs. Groups are organized by category (ADL,
 * medication, clinical, nutrition, social, safety) and contain
 * ordered child task templates.
 */

import { useMemo } from 'react';
import { gql, useQuery } from '@ferp/graphql-client';

// ─── GraphQL Query ────────────────────────────────────────────────────────────

export const TASK_GROUPS_QUERY = gql`
  query TaskGroups($subgraph: String, $setting: String, $category: TaskGroupCategory) {
    taskGroups(subgraph: $subgraph, setting: $setting, category: $category) {
      id
      configId
      name
      category
      description
      icon
      color
      isLifeCritical
      completionStrategy
      schedule
      tags
      subgraph
      setting
      children {
        id
        name
        taskType
        priority
        assigneeRole
        estimatedDurationMinutes
        sequenceOrder
        cardinality
        cardinalitySource
        required
        dependsOn
        complianceFrameworks
        condition
        isLifeCritical
        assistLevels
        note
      }
    }
  }
`;

export const TASK_GROUP_CATEGORIES_QUERY = gql`
  query TaskGroupCategories($subgraph: String, $setting: String) {
    taskGroupCategories(subgraph: $subgraph, setting: $setting) {
      id
      name
      description
      icon
      color
      groupCount
    }
  }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChildTask {
  id: string;
  name: string;
  taskType: string;
  priority: string;
  assigneeRole: string | null;
  estimatedDurationMinutes: number | null;
  sequenceOrder: number;
  cardinality: string;
  cardinalitySource: string | null;
  required: boolean;
  dependsOn: string[];
  complianceFrameworks: string[];
  condition: string | null;
  isLifeCritical: boolean;
  assistLevels: string | null;
  note: string | null;
}

export interface TaskGroup {
  id: string;
  configId: string;
  name: string;
  category: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  isLifeCritical: boolean;
  completionStrategy: string;
  schedule: string | null;
  tags: string[];
  subgraph: string;
  setting: string;
  children: ChildTask[];
}

export interface TaskGroupCategoryInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  groupCount: number;
}

export interface TaskGroupFilters {
  subgraph?: string;
  setting?: string;
  category?: string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTaskGroups(filters?: TaskGroupFilters) {
  const { data, loading, error, refetch } = useQuery<{
    taskGroups: TaskGroup[];
  }>(TASK_GROUPS_QUERY, {
    variables: filters ?? {},
    errorPolicy: 'all',
  });

  const taskGroups = data?.taskGroups ?? [];

  const groupsByCategory = useMemo(() => {
    const map: Record<string, TaskGroup[]> = {};
    for (const group of taskGroups) {
      const cat = group.category;
      if (!map[cat]) map[cat] = [];
      map[cat].push(group);
    }
    return map;
  }, [taskGroups]);

  const lifeCriticalGroups = useMemo(
    () => taskGroups.filter((g) => g.isLifeCritical),
    [taskGroups],
  );

  const totalChildTasks = useMemo(
    () => taskGroups.reduce((sum, g) => sum + g.children.length, 0),
    [taskGroups],
  );

  return {
    taskGroups,
    groupsByCategory,
    lifeCriticalGroups,
    totalChildTasks,
    loading,
    error,
    refetch,
  };
}

export function useTaskGroupCategories(filters?: Omit<TaskGroupFilters, 'category'>) {
  const { data, loading, error } = useQuery<{
    taskGroupCategories: TaskGroupCategoryInfo[];
  }>(TASK_GROUP_CATEGORIES_QUERY, {
    variables: filters ?? {},
    errorPolicy: 'all',
  });

  return {
    categories: data?.taskGroupCategories ?? [],
    loading,
    error,
  };
}
